import {
  BuildingOffice,
  CaretRight,
  HandWaving,
  PlusCircle,
  SlidersHorizontal,
} from "@phosphor-icons/react";
import { Button, Checkbox, Label, TextField } from "@replicate/ui";
import Cookies from "js-cookie";
import { useState } from "react";
import { useForm } from "react-hook-form";

const ONBOARDING_SURVEY_PROVENANCE_OPTIONS = [
  {
    name: "Search",
    value: "search",
  },
  {
    name: "YouTube",
    value: "youtube",
  },
  {
    name: "X (Twitter)",
    value: "twitter",
  },
  {
    name: "Instagram",
    value: "instagram",
  },
  {
    name: "TikTok",
    value: "tiktok",
  },
  {
    name: "Facebook",
    value: "facebook",
  },
  {
    name: "Reddit",
    value: "reddit",
  },
  {
    name: "LinkedIn",
    value: "linkedin",
  },
  {
    name: "Discord",
    value: "discord",
  },
  {
    name: "At an event",
    value: "event",
  },
  {
    name: "Hacker News",
    value: "hackernews",
  },
  {
    name: "Word of mouth",
    value: "word_of_mouth",
  },
  {
    name: "Other",
    value: "other",
  },
];
const USER_SURVEY_ENTITY_TYPE_OPTIONS = [
  {
    name: "For personal use",
    value: "personal",
  },
  {
    name: "For business use",
    value: "business",
  },
  {
    name: "For academic use",
    value: "academic",
  },
];

const USER_SURVEY_USAGE_INTENT_OPTIONS = [
  {
    name: "Adding AI to an existing application",
    value: "existing_app",
  },
  {
    name: "Using AI in a new application",
    value: "new_app",
  },
  {
    name: "Uploading a private AI model",
    value: "private_model",
  },
  {
    name: "Exploring new models",
    value: "exploring",
  },
  {
    name: "Fine-tuning existing models",
    value: "fine_tuning",
  },
  {
    name: "Other",
    value: "other",
  },
];

const USER_SURVEY_PREFERRED_PROGRAMMING_LANGUAGE = [
  {
    name: "Python",
    value: "python",
  },
  {
    name: "JavaScript",
    value: "javascript",
  },
  {
    name: "TypeScript",
    value: "typescript",
  },
  {
    name: "Go",
    value: "go",
  },
  {
    name: "Elixir",
    value: "elixir",
  },
  {
    name: "Swift",
    value: "swift",
  },
  {
    name: "Other",
    value: "other",
  },
];

const Step1EmailPreferences = ({
  userJoinedToday,
  setCurrentStep,
  defaultUserEmail,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm();
  const onSubmit = async (data) => {
    const url = "/users/onboarding/email";
    const formData = new FormData();
    formData.append("email", data.email);

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRFToken": Cookies.get("csrftoken") ?? "",
      },
      body: formData,
    });

    if (res.ok) {
      setCurrentStep(2);
    } else {
      const errors = await res.json();
      for (const key of Object.keys(errors)) {
        setError(key, {
          type: "manual",
          message: errors[key][0],
        });
      }
    }
  };

  return (
    <section>
      <h3 className="mb-lh flex items-center gap-2">
        <HandWaving />{" "}
        <span>{userJoinedToday ? "Welcome to Replicate" : "Welcome back"}</span>
        <span className="text-r8-gray-8">(1 / 3)</span>
      </h3>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-2 space-y-2">
          <Label htmlFor="id_email">
            What is your email address? If you're using Replicate for work,
            enter your work email.
          </Label>
          <TextField
            id="id_email"
            maxLength={255}
            placeholder="Email"
            required
            type="email"
            defaultValue={defaultUserEmail}
            {...register("email", {
              required: "Email is required",
              pattern: { value: /^\S+@\S+$/i, message: "Invalid email format" },
            })}
          />
          {errors.email && (
            <p className="text-r8-sm text-r8-red-10">
              {errors.email.message?.toString()}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <Button startIcon={<CaretRight />} type="submit" className="mt-2">
            Save email preferences
          </Button>
        </div>
      </form>
    </section>
  );
};

const Step2UserOnboardingSurvey = ({ setCurrentStep }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    const url = "/users/onboarding";
    const formData = new FormData();
    formData.append("usage_type", data.usage_type);
    formData.append("usage_intent", data.usage_intent);
    formData.append("discovery_method", data.discovery_method);
    formData.append("user_research_opt_in", data.user_research_opt_in);
    formData.append(
      "preferred_programming_language",
      data.preferred_programming_language
    );

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRFToken": Cookies.get("csrftoken") ?? "",
      },
      body: formData,
    });

    if (res.ok) {
      setCurrentStep(3);
    }
  };

  const onDismiss = async (data) => {
    const url = "/users/onboarding/dismiss";
    const formData = new FormData();
    formData.append("dismiss", "true");

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRFToken": Cookies.get("csrftoken") ?? "",
      },
      body: formData,
    });

    if (res.ok) {
      setCurrentStep(3);
    }
  };

  return (
    <section>
      <h3 className="mb-lh flex items-center gap-2">
        <SlidersHorizontal /> <span>Your preferences</span>
        <span className="text-r8-gray-8">(2 / 3)</span>
      </h3>
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div className="mb-2">
            <Label htmlFor="id_usage_type">
              What best describes how you are intending to use Replicate?
            </Label>
            <div className="[&_select]:w-full tw-select my-2 space-y-2">
              <select
                id="id_usage_type"
                className="w-full my-2"
                {...register("usage_type", {
                  required: "This field is required",
                })}
              >
                <option value="">Please select one</option>
                {USER_SURVEY_ENTITY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
              {errors.usage_type && (
                <p className="text-r8-sm text-r8-red-10">
                  {errors.usage_type.message?.toString()}
                </p>
              )}
            </div>
          </div>
          <div className="mb-2">
            <Label htmlFor="id_preferred_programming_language">
              What is your preferred programming language?
            </Label>
            <div className="[&_select]:w-full tw-select my-2 space-y-2">
              <select
                id="id_preferred_programming_language"
                className="w-full my-2"
                {...register("preferred_programming_language", {
                  required: "This field is required",
                })}
              >
                <option value="">Please select one</option>
                {USER_SURVEY_PREFERRED_PROGRAMMING_LANGUAGE.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
              {errors.preferred_programming_language && (
                <p className="text-r8-sm text-r8-red-10">
                  {errors.preferred_programming_language.message?.toString()}
                </p>
              )}
            </div>
          </div>
          <div className="mb-2">
            <Label htmlFor="id_usage_intent">
              What are you going to use Replicate for?
            </Label>
            <div className="[&_select]:w-full tw-select my-2 space-y-2">
              <select
                id="id_usage_intent"
                className="w-full my-2"
                {...register("usage_intent", {
                  required: "This field is required",
                })}
              >
                <option value="">Please select one</option>
                {USER_SURVEY_USAGE_INTENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
              {errors.usage_intent && (
                <p className="text-r8-sm text-r8-red-10">
                  {errors.usage_intent.message?.toString()}
                </p>
              )}
            </div>
          </div>
          <div className="mb-2">
            <Label htmlFor="id_discovery_method">
              Where did you learn about Replicate?
            </Label>
            <div className="[&_select]:w-full tw-select my-2 space-y-2">
              <select
                id="id_discovery_method"
                className="w-full my-2"
                {...register("discovery_method", {
                  required: "This field is required",
                })}
              >
                <option value="">Please select one</option>
                {ONBOARDING_SURVEY_PROVENANCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
              {errors.discovery_method && (
                <p className="text-r8-sm text-r8-red-10">
                  {errors.discovery_method.message?.toString()}
                </p>
              )}
            </div>
          </div>
          <div>
            <Label
              className="inline-flex items-center mb-2"
              htmlFor="id_user_research_opt_in"
            >
              <Checkbox
                {...register("user_research_opt_in")}
                id="id_user_research_opt_in"
                type="checkbox"
              />
              <span className="ml-2">
                I’m interested in participating in user research
              </span>
            </Label>
            <hr className="my-5" />
          </div>
          <div className="flex justify-between items-center mt-4">
            <Button type="button" variant="outlined" onClick={onDismiss}>
              Skip this
            </Button>
            <Button
              startIcon={<CaretRight />}
              type="submit"
              className="ml-auto"
            >
              Save personal preferences
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
};

const Step3CreateOrganization = () => {
  return (
    <section>
      <h3 className="mb-lh flex items-center gap-2">
        <BuildingOffice /> <span>Your organizations</span>
        <span className="text-r8-gray-8">(3 / 3)</span>
      </h3>
      <div className="space-y-6">
        <div className="space-y-4">
          <h4 className="font-sans text-r8-lg">
            Are you a company? We recommend creating an organization to make it
            easier to collaborate with your teammates on Replicate.
          </h4>

          <div className="text-r8-sm space-y-3">
            <p>You can always make an organization later on.</p>
          </div>
          <Button
            type="button"
            variant="outlined"
            startIcon={<PlusCircle />}
            /* biome-ignore lint/a11y/useAnchorContent: handled by ariakit */
            render={<a href="/orgs/join" />}
          >
            Join or create an organization
          </Button>
        </div>
        <hr className="my-5" />
        <div className="flex justify-end items-center mt-4">
          <Button
            startIcon={<CaretRight />}
            // biome-ignore lint/a11y/useAnchorContent: handled by ariakit
            render={<a href="/dashboard" />}
            type="submit"
            variant="filled"
            className="ml-auto"
          >
            View your dashboard
          </Button>
        </div>
      </div>
    </section>
  );
};

export default function OnboardingUserSurvey({
  userJoinedToday,
  currentUserEmail,
}: {
  userJoinedToday: boolean;
  currentUserEmail: string;
}) {
  const [currentStep, setCurrentStep] = useState(1);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1EmailPreferences
            userJoinedToday={userJoinedToday}
            setCurrentStep={setCurrentStep}
            defaultUserEmail={currentUserEmail}
          />
        );
      case 2:
        return <Step2UserOnboardingSurvey setCurrentStep={setCurrentStep} />;
      case 3:
        return <Step3CreateOrganization />;
      default:
        return (
          <Step1EmailPreferences
            userJoinedToday={userJoinedToday}
            setCurrentStep={setCurrentStep}
            defaultUserEmail={currentUserEmail}
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-8 max-w-2xl">
      <div className="content-container">{renderStep()}</div>
    </div>
  );
}
